import { trackKey, isPlayable } from '../../../../services/studentTracksModel';
import { jobTone, jobStatusText, visibleJobs } from '../../../../services/trackJobsModel';

/**
 * Pure helpers for the song tab's track strip and the Song panel's Tracks mode
 * (Lesson Dashboard pass 3, batches E and F, 2026-09-28). No React, no
 * Firestore: every decision the strip makes about WHAT to show lives here so
 * it can be tested without a player or a page.
 */

const toMillis = (v) => {
	if (v == null) return null;
	if (typeof v === 'number') return v;
	if (typeof v.toMillis === 'function') return v.toMillis();
	if (typeof v.toDate === 'function') return v.toDate().getTime();
	if (v instanceof Date) return v.getTime();
	if (typeof v.seconds === 'number') return v.seconds * 1000;
	return null;
};

/**
 * E5: THE STRIP FOLLOWS WHAT IS PLAYING, NOT THE TAB (Anthony, 2026-09-27:
 * "the controls and scrub bar should stay visible"). While a track is loaded
 * in the page's one player, row A shows ITS transport, labelled with its own
 * song, even when the teacher has moved to another tab, so what is sounding
 * can always be seen and stopped. Row B (the cut pills) is always the current
 * tab's. With nothing loaded, row A belongs to the current tab.
 *
 * @param {object} args
 * @param {object|null} args.current   player.current ({ key, file, engine })
 * @param {Array}  args.tabFiles       the current tab's staff-visible files
 * @param {Array}  args.trackDocs      every studentTracks doc for the student
 * @param {string} args.tabSongName    the current tab's song name
 * @returns {{ file: object|null, songName: string, otherSong: boolean }}
 *   `file` is the loaded file (null when nothing is), `songName` the song it
 *   belongs to, `otherSong` true when that is NOT the current tab.
 */
export const stripSubject = ({ current = null, tabFiles = [], trackDocs = [], tabSongName = '' } = {}) => {
	if (!current || !current.file) return { file: null, songName: tabSongName || '', otherSong: false };
	const key = current.key || trackKey(current.file);
	const onTab = (Array.isArray(tabFiles) ? tabFiles : []).some((f) => trackKey(f) === key);
	if (onTab) return { file: current.file, songName: tabSongName || '', otherSong: false };
	// Raw files, not staffVisibleFiles: a file that is PLAYING is reachable by
	// definition, and the visibility filter can only lose the match.
	const doc = (Array.isArray(trackDocs) ? trackDocs : []).find(
		(d) => d && Array.isArray(d.files) && d.files.some((f) => f && trackKey(f) === key)
	);
	return { file: current.file, songName: (doc && doc.songName) || 'another song', otherSong: true };
};

/** The cut the big play button starts when nothing on this tab is loaded:
 *  the song's recital track if it is playable here, else the first playable
 *  cut. null when the tab has nothing to play. */
export const defaultCut = (files = [], song = null) => {
	const playable = (Array.isArray(files) ? files : []).filter(isPlayable);
	if (!playable.length) return null;
	const recital = song && song.recitalTrack && song.recitalTrack.storagePath;
	return (recital && playable.find((f) => f.storagePath === recital)) || playable[0];
};

/** F2: a filed cut is NEW for ten minutes after the job that made it lands. */
export const NEW_BADGE_MS = 10 * 60 * 1000;

/** Keys of the files a job filed in the last NEW_BADGE_MS. Files carry the
 *  `jobId` of the job that made them (functions/trackJobs.js). */
export const newFileKeys = (files = [], jobs = [], nowMs = Date.now()) => {
	const fresh = new Set(
		(Array.isArray(jobs) ? jobs : [])
			.filter((j) => j && j.status === 'done' && j.pointerId)
			.filter((j) => {
				const at = toMillis(j.filedAt) ?? toMillis(j.updatedAt);
				return at != null && nowMs - at < NEW_BADGE_MS;
			})
			.map((j) => j.id)
	);
	return new Set(
		(Array.isArray(files) ? files : []).filter((f) => f && f.jobId && fresh.has(f.jobId)).map((f) => trackKey(f))
	);
};

/**
 * F2: how far along a vocal-removal job probably is, 0..1, or null when
 * there is nothing honest to pace it by. Paced by the song's own length
 * (upload jobs carry `source.durationSec`) from the moment a worker claimed
 * it: a studio Mac takes about half the song (BS-RoFormer, measured 107 s
 * for a 213 s song), the cloud about the whole song. Capped at 0.95 so the
 * bar never claims to be finished before the job says it is.
 */
export const vocalProgress = (job, nowMs = Date.now()) => {
	if (!job || job.status !== 'claimed' || !job.removeVocals) return null;
	const secs = Number(job.source && job.source.durationSec);
	const start = toMillis(job.claim && job.claim.claimedAt);
	if (!Number.isFinite(secs) || secs <= 0 || start == null) return null;
	const expectedMs = secs * 1000 * (job.claim && job.claim.kind === 'cloud' ? 1 : 0.5);
	return Math.max(0, Math.min(0.95, (nowMs - start) / expectedMs));
};

/** Whole minutes left on the paced estimate, at least 1; null when unknown. */
const minutesLeft = (job, nowMs) => {
	const secs = Number(job && job.source && job.source.durationSec);
	const start = toMillis(job && job.claim && job.claim.claimedAt);
	if (!Number.isFinite(secs) || secs <= 0 || start == null) return null;
	const expectedMs = secs * 1000 * (job.claim && job.claim.kind === 'cloud' ? 1 : 0.5);
	return Math.max(1, Math.round((start + expectedMs - nowMs) / 60000));
};

/**
 * The strip's job chip (E1, F3, F4): one short line for the jobs worth
 * seeing now. null when there are none.
 *   failed anywhere   -> { tone: 'failed', text: 'Track failed · Please tell Anthony' }
 *   two or more       -> { tone: 'working', text: '2 jobs · about 3 min' } (estimate only when known)
 *   one working       -> { tone: 'working', text: its status line }
 *   one just filed    -> { tone: 'done',    text: its status line }
 */
export const jobChip = (jobs = [], { nowMs = Date.now(), anyMacOn = true, dismissed = new Set() } = {}) => {
	// A stopped job keeps its line in Working now for 90 s, but the strip's
	// chip clears at once (pass 4 S5.3).
	const rows = visibleJobs(jobs, nowMs, dismissed).filter((j) => jobTone(j) !== 'stopped');
	if (!rows.length) return null;
	if (rows.some((j) => jobTone(j) === 'failed')) return { tone: 'failed', text: 'Track failed · Please tell Anthony', count: rows.length };
	const working = rows.filter((j) => jobTone(j) === 'working');
	if (working.length >= 2) {
		const mins = working.map((j) => minutesLeft(j, nowMs)).filter((m) => m != null);
		const tail = mins.length ? ` · about ${Math.max(...mins)} min` : '';
		return { tone: 'working', text: `${working.length} jobs${tail}`, count: rows.length };
	}
	const one = working[0] || rows[0];
	return { tone: jobTone(one), text: jobStatusText(one, { anyMacOn }), count: rows.length };
};
